// ===== WhatsApp-style Chat List Component =====

import React, { memo, useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Avatar, 
  List, 
  ListItem, 
  ListItemAvatar, 
  ListItemText,
  Badge,
  InputBase,
  IconButton,
  Divider
} from '@mui/material';
import { 
  Search, 
  Message, 
  MoreVert,
  Phone,
  VideoCall
} from '@mui/icons-material';
import { sanitizeInput } from '../utils/ValidationUtils';

const ChatListItem = memo(({ chat, isActive, onClick }) => {
  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);
    
    if (diffInHours < 24) {
      return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
    } else if (diffInHours < 168) { // Less than a week
      return date.toLocaleDateString('en-US', { weekday: 'short' });
    } else {
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric' 
      });
    }
  };

  const truncateMessage = (message, maxLength = 30) => {
    if (!message || message.trim() === '') return 'New message';
    if (message.length <= maxLength) return message;
    return message.substring(0, maxLength) + '...';
  };

  return (
    <ListItem
      button
      onClick={onClick}
      style={{
        backgroundColor: isActive ? '#e8f5e8' : 'transparent',
        borderRadius: '8px',
        margin: '2px 8px',
        padding: '12px 16px',
        cursor: 'pointer',
        transition: 'background-color 0.2s ease',
        '&:hover': {
          backgroundColor: isActive ? '#e8f5e8' : '#f5f5f5'
        }
      }}
    >
      <ListItemAvatar>
        <Badge
          overlap="circular"
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          badgeContent={
            chat.isOnline ? (
              <Box
                style={{
                  width: '12px',
                  height: '12px',
                  backgroundColor: '#25D366',
                  borderRadius: '50%',
                  border: '2px solid white'
                }}
              />
            ) : null
          }
        >
          <Avatar
            style={{
              width: '50px',
              height: '50px',
              backgroundColor: '#128C7E',
              fontSize: '18px',
              fontWeight: 'bold'
            }}
          >
            {(chat.name || 'U').charAt(0).toUpperCase()}
          </Avatar>
        </Badge>
      </ListItemAvatar>
      
      <ListItemText
        primary={
          <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography
              variant="subtitle1"
              style={{
                fontWeight: isActive ? '600' : '500',
                fontSize: '16px',
                color: '#000',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '180px'
              }}
            >
              {sanitizeInput(chat.name || 'Unknown User')}
            </Typography>
            <Typography
              variant="caption"
              style={{
                fontSize: '12px',
                color: '#666',
                minWidth: 'fit-content'
              }}
            >
              {formatTime(chat.lastMessageTime)}
            </Typography>
          </Box>
        }
        secondary={
          <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '4px' }}>
            <Typography
              variant="body2"
              style={{
                fontSize: '14px',
                color: '#666',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '180px'
              }}
            >
              {chat.lastMessage?.type === 'image' ? '📷 Photo' :
               chat.lastMessage?.type === 'audio' ? '🎵 Audio' :
               chat.lastMessage?.type === 'file' ? '📎 File' :
               truncateMessage(chat.lastMessage?.text)}
            </Typography>
            {chat.unreadCount > 0 && (
              <Badge
                badgeContent={chat.unreadCount > 99 ? '99+' : chat.unreadCount}
                style={{
                  '& .MuiBadge-badge': {
                    backgroundColor: '#25D366',
                    color: 'white',
                    fontSize: '11px',
                    height: '20px',
                    minWidth: '20px'
                  }
                }}
              />
            )}
          </Box>
        }
      />
    </ListItem>
  );
});

const ChatList = ({ chats, activeChat, onChatSelect, currentUser }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredChats, setFilteredChats] = useState(chats);

  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredChats(chats);
    } else {
      const filtered = chats.filter(chat =>
        chat.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        chat.lastMessage?.text?.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredChats(filtered);
    }
  }, [chats, searchQuery]);

  const handleStartNewChat = () => {
    // This would typically open a contact selector or new chat dialog
    console.log('Start new chat clicked');
  };

  return (
    <Box
      style={{
        width: '320px',
        minWidth: '320px',
        height: '100%',
        backgroundColor: '#fff',
        borderRight: '1px solid #e0e0e0',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      {/* Header */}
      <Box
        style={{
          padding: '16px 20px',
          backgroundColor: '#f8f9fa',
          borderBottom: '1px solid #e0e0e0'
        }}
      >
        <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <Typography
            variant="h5"
            style={{
              fontWeight: '600',
              color: '#000',
              fontSize: '22px'
            }}
          >
            Chats
          </Typography>
          <Box>
            <IconButton
              onClick={handleStartNewChat}
              style={{ color: '#128C7E', marginRight: '4px' }}
              title="New chat"
            >
              <Message />
            </IconButton>
            <IconButton
              style={{ color: '#666' }}
              title="More options"
            >
              <MoreVert />
            </IconButton>
          </Box>
        </Box>

        {/* Search Bar */}
        <Box
          style={{
            display: 'flex',
            alignItems: 'center',
            backgroundColor: '#f0f2f5',
            borderRadius: '20px',
            padding: '8px 16px',
            border: '1px solid #e4e6ea'
          }}
        >
          <Search style={{ color: '#65676b', marginRight: '8px', fontSize: '18px' }} />
          <InputBase
            placeholder="Search chats..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              flex: 1,
              fontSize: '14px',
              color: '#000'
            }}
          />
        </Box>
      </Box>

      {/* Chat List */}
      <Box style={{ flex: 1, overflowY: 'auto' }}>
        {filteredChats.length === 0 ? (
          <Box
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '300px',
              padding: '20px',
              textAlign: 'center',
              color: '#666'
            }}
          >
            <Message style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.5 }} />
            <Typography variant="h6" style={{ marginBottom: '8px', fontSize: '18px' }}>
              {searchQuery ? 'No chats found' : 'No conversations yet'}
            </Typography>
            <Typography variant="body2" style={{ fontSize: '14px' }}>
              {searchQuery 
                ? 'Try searching for a different name or message'
                : 'Start a conversation to see your chats here'
              }
            </Typography>
          </Box>
        ) : (
          <List style={{ padding: '8px 0' }}>
            {filteredChats.length > 0 ? (
              filteredChats.map((chat) => (
                <React.Fragment key={chat.id}>
                  <ChatListItem
                    chat={chat}
                    isActive={activeChat?.id === chat.id}
                    onClick={() => onChatSelect(chat)}
                  />
                  <Divider variant="inset" style={{ marginLeft: '76px', marginRight: '16px' }} />
                </React.Fragment>
              ))
            ) : (
              <Box
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '200px',
                  padding: '20px',
                  textAlign: 'center',
                  color: '#666'
                }}
              >
                <Message style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.5 }} />
                <Typography variant="body2" style={{ fontSize: '14px' }}>
                  No conversations yet
                </Typography>
              </Box>
            )}
          </List>
        )}
      </Box>

      {/* Footer with user info */}
      <Box
        style={{
          padding: '12px 16px',
          backgroundColor: '#f8f9fa',
          borderTop: '1px solid #e0e0e0',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}
      >
        <Avatar
          style={{
            width: '36px',
            height: '36px',
            backgroundColor: '#25D366',
            fontSize: '14px'
          }}
        >
          {(currentUser?.name || 'U').charAt(0).toUpperCase()}
        </Avatar>
        <Box style={{ flex: 1 }}>
          <Typography
            variant="subtitle2"
            style={{
              fontSize: '14px',
              fontWeight: '500',
              color: '#000'
            }}
          >
            {sanitizeInput(currentUser?.name || 'User')}
          </Typography>
          <Typography
            variant="caption"
            style={{
              fontSize: '12px',
              color: '#666'
            }}
          >
            Online
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default memo(ChatList);
